import React, { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { Pallet } from "../../../../front-office/model/pallet";
import { Parcel } from "../../../../front-office/model/parcel";
import { AddScannedPalletCommand } from "./command/add-scanned-pallet-command";
import { AddScannedParcelCommand } from "./command/add-scanned-parcel-command";
import { OperationProduct } from "../../../../front-office/model/operation-product";
import { ScannedPalletsList } from "./scanned-pallets-list";
import { ScannedParcelsList } from "./scanned-parcels-list";
import { ScannedProductsList } from "./scanned-products-list";
import { AddScannedProductCommand } from "./command/add-scanned-product-command";
import { ScanningSessionRepository } from "./scanning-session-repository";
import { PreviousScanningSession } from "./previous-scanning-session";
import { AnyOfFetchPreviousScanningSessionDetailsResult } from "../../../screen/restore-previous-scanning-session-screen/command/fetch-previous-scanning-session-details-result";
import { AnyOfAddScannedItemCommand } from "./command/add-scanned-item-command";
import { AnyOfScannedPalletsList } from "./scanned-items-list";

interface IScanningSessionBaseContext {
    lastSavedScan: string | undefined;
    clear: () => void;
    resetWith: (command: AnyOfAddScannedItemCommand) => void;
    previousSession: PreviousScanningSession | undefined;
    restorePreviousSession: (data: AnyOfFetchPreviousScanningSessionDetailsResult) => void;
}

interface IPalletScanningSessionContext extends IScanningSessionBaseContext {
    flow: "pallet";
    items: Readonly<Pallet[]>;
    add: (command: AddScannedPalletCommand) => boolean;
    remove: (item: Pallet) => void;
}

interface IParcelScanningSessionContext extends IScanningSessionBaseContext {
    flow: "parcel";
    items: Readonly<Parcel[]>;
    add: (command: AddScannedParcelCommand) => boolean;
    remove: (item: Parcel) => void;
}

interface IProductScanningSessionContext extends IScanningSessionBaseContext {
    flow: "product";
    items: Readonly<OperationProduct[]>;
    add: (command: AddScannedProductCommand) => boolean;
    remove: (item: OperationProduct) => void;
}

export interface IEmptyScanningSessionContext extends IScanningSessionBaseContext {
    flow: undefined;
    items: never[];
    add: (item: AnyOfAddScannedItemCommand) => boolean;
    remove: () => void;
}

type IScanningSessionContext = IPalletScanningSessionContext | IParcelScanningSessionContext | IProductScanningSessionContext | IEmptyScanningSessionContext;

const defaultScannedItemsContext = {
    flow: undefined,
    items: [],
    lastSavedScan: localStorage.getItem("scanning_session_repository") ?? undefined,
    add: () => true,
    previousSession: undefined,
    restorePreviousSession: () => ({}),
    remove: () => ({}),
    clear: () => ({}),
    resetWith: () => ({}),
};

const ScanningSessionContext = createContext<IScanningSessionContext>(defaultScannedItemsContext);

export const ScanningSessionContextProvider = ({ children }: { children: ReactNode }) => {
    const [scannedItemsList, setScannedItemsList] = useState<AnyOfScannedPalletsList | undefined>();
    const recentScanningSessionRepository = useMemo(() => new ScanningSessionRepository(), []);

    const handleCreateScannedItemsList = (command: AnyOfAddScannedItemCommand) => {
        switch (command.type) {
            case "pallet":
                recentScanningSessionRepository.add(command.item.code, "pallet", command.warehouseCode);
                setScannedItemsList(new ScannedPalletsList([command.item]));
                break;
            case "parcel":
                recentScanningSessionRepository.add(command.item.parcelId.toString(), "parcel", command.warehouseCode);
                setScannedItemsList(new ScannedParcelsList([command.item]));
                break;
            case "product":
                recentScanningSessionRepository.add(command.item.productId.toString(), "parcel", command.warehouseCode);
                setScannedItemsList(new ScannedProductsList([command.item]));
                break;
        }
    };

    const handleRestorePreviousScannedItemsList = (command: AnyOfFetchPreviousScanningSessionDetailsResult) => {
        switch (command.flow) {
            case "pallet":
                setScannedItemsList(new ScannedPalletsList(command.items));
                break;
            case "parcel":
                setScannedItemsList(new ScannedParcelsList(command.items));
                break;
            case "product":
                setScannedItemsList(new ScannedProductsList(command.items));
                break;
        }
    };

    const handleAddItemToScannedItemsList = <T extends AnyOfAddScannedItemCommand>(command: T) => {
        let result = false;
        switch (command.type) {
            case "pallet":
                recentScanningSessionRepository.add(command.item.code, "pallet", command.warehouseCode);
                setScannedItemsList((prev: ScannedPalletsList) => {
                    const list = prev.add(command.item);
                    if (list !== prev) {
                        result = true;

                        return list;
                    }

                    return prev;
                });
                break;
            case "parcel":
                recentScanningSessionRepository.add(command.item.parcelId.toString(), "parcel", command.warehouseCode);
                setScannedItemsList((prev: ScannedParcelsList) => {
                    const list = prev.add(command.item);
                    if (list !== prev) {
                        result = true;

                        return list;
                    }

                    return prev;
                });
                break;
            case "product":
                recentScanningSessionRepository.add(command.item.productId.toString(), "product", command.warehouseCode);
                setScannedItemsList((prev: ScannedProductsList) => {
                    const list = prev.add(command.item);
                    if (list !== prev) {
                        result = true;

                        return list;
                    }

                    return prev;
                });
                break;
        }

        return result;
    };

    const handleRemovePallet = (previousList: ScannedPalletsList, pallet: Pallet) => {
        recentScanningSessionRepository.remove(pallet.code);
        const newList = previousList.remove(pallet);

        if (newList !== previousList) {
            setScannedItemsList(newList);
            return true;
        }

        return false;
    };

    const handleRemoveParcel = (previousList: ScannedParcelsList, parcel: Parcel) => {
        recentScanningSessionRepository.remove(parcel.parcelId.toString());
        const newList = previousList.remove(parcel);

        if (newList !== previousList) {
            setScannedItemsList(newList);
            return true;
        }

        return false;
    };

    const handleRemoveProduct = (previousList: ScannedProductsList, product: OperationProduct) => {
        recentScanningSessionRepository.remove(product.productId.toString());
        const newList = previousList.remove(product);

        if (newList !== previousList) {
            setScannedItemsList(newList);
            return true;
        }

        return false;
    };

    const handleClearScannedItemsList = () => {
        setScannedItemsList(undefined);
        recentScanningSessionRepository.clear();
    };

    const handleResetScannedItemsList = (command: AnyOfAddScannedItemCommand) => {
        recentScanningSessionRepository.clear();
        handleCreateScannedItemsList(command);
    };

    const baseContextValue = {
        lastSavedScan: defaultScannedItemsContext.lastSavedScan,
        previousSession: recentScanningSessionRepository.flow !== "unknown" ?
            new PreviousScanningSession(recentScanningSessionRepository.locatorList,
                recentScanningSessionRepository.flow, recentScanningSessionRepository.warehouseCode) :
            undefined,
        restorePreviousSession: handleRestorePreviousScannedItemsList,
        clear: handleClearScannedItemsList,
        resetWith: handleResetScannedItemsList,
    };
    const contextValue = scannedItemsList?.type === "pallet" ?
        {
            flow: "pallet",
            items: scannedItemsList.items,
            add: handleAddItemToScannedItemsList,
            remove: pallet => handleRemovePallet(scannedItemsList, pallet),
            ...baseContextValue,
        } as IPalletScanningSessionContext :
        scannedItemsList?.type === "parcel" ?
            {
                flow: "parcel",
                items: scannedItemsList.items,
                add: handleAddItemToScannedItemsList,
                remove: parcel => handleRemoveParcel(scannedItemsList, parcel),
                ...baseContextValue,
            } as IParcelScanningSessionContext :
            scannedItemsList?.type === "product" ?
                {
                    flow: "product",
                    items: scannedItemsList.items,
                    add: handleAddItemToScannedItemsList,
                    remove: product => handleRemoveProduct(scannedItemsList, product),
                    ...baseContextValue,
                } as IProductScanningSessionContext :
                {
                    flow: undefined,
                    items: [],
                    add: item => {
                        handleCreateScannedItemsList(item);

                        return true;
                    },
                    ...baseContextValue,
                } as IEmptyScanningSessionContext;

    return <ScanningSessionContext.Provider value={contextValue}>
        {children}
    </ScanningSessionContext.Provider>;
};

export const useScanningSession = () => {
    return useContext(ScanningSessionContext);
};
